import { ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';
import { DeleteOutline } from '@mui/icons-material';
import styled from '@emotion/styled';
import { uiTheme as t } from './theme';
import Button from './Button';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  cancelLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  /**
   * Icono del círculo. Por omisión, el bote de basura.
   */
  icon?: ReactNode;
  /**
   * Carácter de la acción. `'danger'` (por omisión) pinta el círculo y el botón
   * en rojo; `'primary'` los pinta en verde, para confirmaciones que no
   * destruyen nada.
   */
  tone?: 'danger' | 'primary';
}

const Content = styled.div({ padding: 28 });

const IconCircle = styled.div<{ tone: 'danger' | 'primary' }>(({ tone }) => ({
  width: 52,
  height: 52,
  borderRadius: '50%',
  background: tone === 'danger' ? t.color.dangerSurface : t.color.countBg,
  color: tone === 'danger' ? t.color.danger : t.color.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 16,
  '& .MuiSvgIcon-root': { fontSize: 27 },
}));

const Title = styled.div({
  fontFamily: t.font,
  fontSize: 18,
  fontWeight: 600,
  color: t.color.title,
});

const Description = styled.p({
  margin: '8px 0 0',
  fontFamily: t.font,
  fontSize: 14,
  lineHeight: 1.55,
  color: t.color.muted,
});

const Actions = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
  marginTop: 24,
});

/**
 * Organismo: diálogo de confirmación. Por omisión es el destructivo de siempre
 * —icono de basura y acción primaria en rojo—, y con `tone="primary"` sirve
 * para confirmar acciones que no destruyen nada sin alarmar de más.
 */
const ConfirmDialog = ({
  open,
  title,
  description,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  icon,
  tone = 'danger',
}: ConfirmDialogProps) => (
  <Dialog
    open={open}
    onClose={onCancel}
    PaperProps={{
      sx: { width: 400, maxWidth: '92vw', borderRadius: '16px', boxShadow: '0 20px 50px rgba(16, 24, 40, 0.25)' },
    }}
  >
    <Content>
      <IconCircle tone={tone}>{icon || <DeleteOutline />}</IconCircle>
      <Title>{title}</Title>
      <Description>{description}</Description>
      <Actions>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button type="button" variant={tone} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </Actions>
    </Content>
  </Dialog>
);

export default ConfirmDialog;
